/**
 * teamLivePolicy — the decisions behind the live Team & Roles surfaces.
 *
 * FIREBASE-FREE by construction (the jsdom leaf-module rule, stated in
 * services/workspaceIndexLive.ts): this module has NO imports at all, so the
 * unit battery can assert every rule here without a Firebase mock. The
 * repository holds `onSnapshot`; the hook holds React; this file holds the
 * rules both of them obey.
 *
 * Background (2026-09-17 owner directive: "team & roles, members, roles
 * invitations usage limits and more needs to be highly responsive and up to
 * date when a user accepts an invitation"). Every Team surface was a one-shot
 * callable fetch on mount, so an admin watching the members tab never saw an
 * invitation get accepted. We now watch the collections purely as a CHANGE
 * SIGNAL and re-run the existing callable — the callables return more than the
 * raw docs (server-joined profile emails, live role member counts, and the
 * limit evaluation the pre-turn gate itself runs), so the server stays the one
 * decider and nothing is recomputed on the client.
 */

/** The four things a Team surface can watch for change. */
export type TeamLiveKind = 'members' | 'invitations' | 'roles' | 'accountUsage';

export const TEAM_LIVE_KINDS: readonly TeamLiveKind[] = [
  'members',
  'invitations',
  'roles',
  'accountUsage',
];

/**
 * What the viewer may read, as firestore.rules decides it.
 *   members       — any account member (rules :820-824)
 *   invitations   — rbacHasPerm(accountId, 'team.view')  (rules :1802)
 *   roles         — rbacHasPerm(accountId, 'roles.view') (rules :1794)
 *   accountUsage  — isVerifiedAccountMember              (rules :855)
 */
export interface TeamLivePermissionView {
  hasTeamView: boolean;
  hasRolesView: boolean;
}

/**
 * Narrow a wanted set down to what this viewer's rules will actually serve.
 *
 * An onSnapshot permission error is TERMINAL in this codebase
 * (chatThreadMessagesRepository.ts:369) — it does not retry, and a listener
 * started against a collection the rules refuse is a dead listener plus a
 * console error on every page open. So we never start one: a member without
 * `team.view` simply keeps today's mount-scoped behaviour on that data.
 */
export function subscribableKinds(
  want: readonly TeamLiveKind[],
  perms: TeamLivePermissionView,
): TeamLiveKind[] {
  const out: TeamLiveKind[] = [];
  for (const kind of want) {
    if (out.includes(kind)) continue;
    if (kind === 'invitations' && !perms.hasTeamView) continue;
    if (kind === 'roles' && !perms.hasRolesView) continue;
    if (!TEAM_LIVE_KINDS.includes(kind)) continue;
    out.push(kind);
  }
  return out;
}

/**
 * Coalescing window for a burst of document writes.
 *
 * Deleting a role re-keys EVERY member doc (roles.ts deleteRoleCore reads all
 * members to re-key them), and accepting an invitation writes the member doc,
 * its role bindings and the invitation doc in one batch — so a single logical
 * change arrives as many snapshot callbacks. One refetch per burst, not one
 * per document.
 */
export const TEAM_LIVE_DEBOUNCE_MS = 400;

/**
 * Floor between two `getMemberLimitStatuses` calls while the "usage now" view
 * is open. That callable evaluates every limited member server-side, and its
 * signal (account_usage) moves on EVERY turn anyone in the org takes — without
 * a floor a busy workspace would call it continuously.
 */
export const USAGE_MIN_REFETCH_INTERVAL_MS = 15_000;

/**
 * The `accounts/{accountId}/account_usage/{docId}` id for the current month.
 *
 * TWIN of monthKeyUtc in functions/src/lib/limitWindows.ts:55 — the client
 * cannot import from functions/, so the lockstep is held by a test that
 * asserts both against the same timestamps. The monthly doc is written inside
 * the SAME transaction as every per-member usage rollup
 * (lib/memberUsageRollup.ts:310-316), which is what makes one document a
 * complete change signal for the whole account's usage. Month rather than day
 * so the subscription re-keys once a month instead of nightly at 00:00 UTC.
 */
export function currentUsageMonthKey(nowMs: number): string {
  return new Date(nowMs).toISOString().slice(0, 7);
}

/** localStorage escape hatch, for turning the listeners off without a deploy. */
export const TEAM_LIVE_DISABLE_KEY = 'prest0_team_live';

/**
 * Live updates are ON unless explicitly switched off. The flag VALUE is passed
 * in rather than read here, so this module stays free of `window`.
 */
export function isTeamLiveEnabled(flagValue: string | null | undefined): boolean {
  return String(flagValue ?? '').trim().toLowerCase() !== 'off';
}
